import classNames from 'classnames';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import TextPlugin from 'gsap/dist/TextPlugin';
import { gsap } from 'gsap/dist/gsap';
import Image from 'next/image';
import { FC, useRef } from 'react';

import styles from './business-hub-good-for.module.scss';
import { SplitChar } from '../splitchar/splitchar';
import { useGSAP } from '@gsap/react';

gsap.registerPlugin(useGSAP);
gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(TextPlugin);

interface Item {
    image: string;
    image2: string;
    title: string;
    item: {
        content: string;
    }[];
}

export interface BusinessHubGoodForProps {
    title: string;
    backgroundColor?: string;
    textColor?: string;
    content: Item[];
}

const BusinessHubGoodFor: FC<BusinessHubGoodForProps> = (props) => {
    const { title, backgroundColor = '#2D3F39', textColor = '#fff', content = [] } = props;

    const containerRef = useRef<HTMLDivElement>(null);

    useGSAP(
        () => {
            const selector = gsap.utils.selector(containerRef.current);
            const sections = gsap.utils.toArray<HTMLElement>(selector(`.${styles.section}`));

            gsap.timeline({
                scrollTrigger: {
                    trigger: containerRef.current,
                    pinnedContainer: containerRef.current,
                    // markers: true,
                    pin: true, // pin the trigger element while active
                    start: 'center center', // when the top of the trigger hits the top of the viewport
                    end: '+=100%',
                    scrub: 1,
                },
            });

            let masterTl = gsap.timeline({
                defaults: { ease: 'none' },
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: 'top center+=40%',
                    end: 'bottom top',
                    scrub: true,
                },
            });

            setMaxHeight(sections, `${styles.section} ${styles.absoluteElem}`);
            gsap.set(sections, { autoAlpha: 0, yPercent: 0, zIndex: (index) => index });

            for (let i = 0; i < sections.length; i++) {
                const section = sections[i];
                const title = gsap.utils.toArray<HTMLDivElement>(gsap.utils.selector(section)(`.${styles.list} .${styles.listTitle}`))[0];

                const prvSection = sections[i - 1];
                if (prvSection) {
                    const prvTitle = gsap.utils.toArray<HTMLDivElement>(gsap.utils.selector(prvSection)(`.${styles.list} .${styles.listTitle}`))[0];
                    const tlOut = animateSection(prvSection, prvTitle, 'out');
                    masterTl.add(tlOut);
                }

                const tlIn = animateSection(section, title, 'in', i === 0 ? '+=1' : '<');
                masterTl.add(tlIn, '-=0.5');
            }
        },
        { scope: containerRef }
    );

    const animateSection = (section: HTMLElement, title: HTMLElement, animationType: 'in' | 'out', position?: gsap.Position) => {
        const sectionSelector = gsap.utils.selector(section);
        const illustrations = gsap.utils.toArray<HTMLDivElement>(sectionSelector(`.${styles.illustration}`));
        const contentItems = gsap.utils.toArray<HTMLDivElement>(sectionSelector(`.${styles.list} span:not(.${styles.invisible})`));
        const titleSplits = gsap.utils.toArray<HTMLDivElement>(gsap.utils.selector(title)(`span`));

        const tl = gsap.timeline({});

        if (animationType === 'in') {
            tl.to(section, { autoAlpha: 1 }, position);
            tl.to(
                titleSplits,
                {
                    duration: 1,
                    autoAlpha: 1,
                    stagger: {
                        from: 'start',
                        each: 0.01,
                    },
                },
                '<'
            );
            tl.fromTo(illustrations, { autoAlpha: 0 }, { autoAlpha: 1, stagger: 0.25 }, '<');
            tl.fromTo(
                contentItems,
                {
                    autoAlpha: 0,
                },
                {
                    duration: 1,
                    autoAlpha: 1,
                    stagger: {
                        from: 'start',
                        each: 0.01,
                    },
                },
                '>-=0.5'
            );
        } else {
            tl.to(
                contentItems,
                {
                    duration: 0.5,
                    autoAlpha: 0,
                    stagger: {
                        from: 'start',
                        each: 0.005,
                    },
                },
                '+=1'
            );
            tl.to(
                titleSplits,
                {
                    duration: 0.5,
                    autoAlpha: 0,
                    stagger: {
                        from: 'start',
                        each: 0.005,
                    },
                },
                '<'
            );
            // tl.to(illustrations, { autoAlpha: 0, stagger: 0.1 }, '<');
        }

        return tl;
    };

    const setMaxHeight = (elements: HTMLElement[], className?: string) => {
        let maxHeightIndex = 0;
        let elemMaxHeight = 0;
        for (let index = 0; index < elements.length; index++) {
            const elem = elements[index];
            if (elem.clientHeight > elemMaxHeight) {
                elemMaxHeight = elem.clientHeight;
                maxHeightIndex = index;
            }
        }

        const smaller = elements.filter((_, index) => index !== maxHeightIndex);
        if (className) {
            gsap.set(smaller, { className });
        }
    };

    return (
        <div ref={containerRef} className={styles.container}>
            <div className={styles.title} dangerouslySetInnerHTML={{ __html: title || '' }} />

            <div className={styles.sectionContainer} style={{ color: textColor, backgroundColor }}>
                <div>
                    {content.map((item, index) => {
                        return (
                            <section key={`section-${index}`} className={styles.section}>
                                <div className={styles.sectionBox}>
                                    <div className={styles.illustrations}>
                                        <div className={classNames(styles.illustration)}>
                                            <Image
                                                src={item.image || ''}
                                                alt=""
                                                style={{ objectFit: 'cover', objectPosition: 'center' }}
                                                fill={true}
                                                loading={'eager'}
                                                sizes="(max-width: 638px) 98vw,  (max-width: 998px) 43vw, 46vw"
                                                loader={({ src, width, quality }) => {
                                                    return `${src}?width=${width}&quality=${quality ?? 75}`;
                                                }}
                                            />
                                        </div>

                                        <div className={styles.list}>
                                            <div className={classNames(styles.listTitle, styles.desktop)}>
                                                <div style={{ whiteSpace: 'nowrap' }} className={classNames(styles.titleItem, styles.titleCommon)}>
                                                    {'Good for your'.split('').map((char, i) => (
                                                        <span key={`${char}-${i}`} style={{ visibility: 'hidden', opacity: 0 }} className={styles.invisible}>
                                                            {char}
                                                        </span>
                                                    ))}
                                                </div>
                                                <div className={classNames(styles.colorMint, styles.titleItem)}>
                                                    {item.title.split('').map((char, i) => (
                                                        <span key={`${char}-${i}`} style={{ visibility: 'hidden', opacity: 0 }} className={styles.invisible}>
                                                            {char}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        <div className={classNames(styles.illustration, styles.right, styles.desktop)}>
                                            <Image
                                                src={item.image2 || ''}
                                                alt=""
                                                style={{ objectFit: 'cover', objectPosition: 'center' }}
                                                sizes="(max-width: 638px) 98vw,  (max-width: 998px) 43vw, 46vw"
                                                fill={true}
                                                loading={'eager'}
                                                loader={({ src, width, quality }) => {
                                                    return `${src}?width=${width}&quality=${quality ?? 75}`;
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div />
                                    <div className={styles.list}>
                                        <div className={styles.listTitle}>
                                            <div>Good for your</div>
                                            <div className={styles.colorMint}>{item.title}</div>
                                        </div>

                                        {item.item.map(({ content }, index) => {
                                            return <SplitChar key={`${index}`} text={content} className={styles.item} />;
                                        })}
                                    </div>
                                    <div />
                                </div>
                            </section>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default BusinessHubGoodFor;
